import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import Footer from './Footer';
import { AuthenticatedFooter } from './AuthenticatedFooter';
import DiscountBanner from './DiscountBanner';
import MobileBottomNav from './MobileBottomNav';
import SuperAdminBar from '../admin/SuperAdminBar';
import MobileBackNav from './MobileBackNav';
import MobileHomeHeader from './MobileHomeHeader';
import ChildMobileHeader from './ChildMobileHeader';
import TalkToDesignerFloat from '../shared/TalkToDesignerFloat';

interface LayoutProps {
  children?: React.ReactNode;
  className?: string;
}


const Layout: React.FC<LayoutProps> = ({ children, className = '' }) => {
  const location = useLocation();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const [sidebarWidth, setSidebarWidth] = React.useState(288); // 288px = w-72
  const [isLargeScreen, setIsLargeScreen] = React.useState(window.innerWidth >= 1024);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = React.useState(false);
  
  // Show sidebar on insights, account pages, profile pages (always), and home/explore/archives/collections (only when authenticated)
  // Hide sidebar on public collection product browsing pages (/collection/:id/products)
  // const isPublicCollectionProducts = location.pathname.match(/^\/collection\/[^/]+\/products/);
  const isCollectionManager = location.pathname.match(/^\/collection\/[^/]+$/) && !location.pathname.includes('/products');
  
  const showSidebar = location.pathname.startsWith('/insights') ||
                      location.pathname.startsWith('/account') ||
                      location.pathname.startsWith('/messages') ||
                      location.pathname.startsWith('/manage-collections') ||
                      location.pathname.startsWith('/store-settings') ||
                      // Buyer surfaces — sidebar on desktop matches every other
                      // authenticated page so layout is consistent.
                      location.pathname.startsWith('/orders') ||
                      location.pathname.startsWith('/favorites') ||
                      (isCollectionManager && isAuthenticated) ||
                      location.pathname.startsWith('/collections/') ||
                      (location.pathname.includes('/social-profile/') && isAuthenticated) ||
                      (location.pathname === '/' && isAuthenticated) ||
                      (location.pathname === '/designers' && isAuthenticated) ||
                      (location.pathname === '/archives' && isAuthenticated);
  
  // Show sidebar toggle (hamburger) on parent pages for mobile when user is authenticated
  const showSidebarToggle = (
    location.pathname.includes('/social-profile/') ||
    location.pathname === '/' ||
    location.pathname === '/designers' ||
    location.pathname === '/archives' ||
    location.pathname === '/orders'
  ) && isAuthenticated;

  // Profile pages hide the sidebar entirely on desktop (Sidebar.tsx uses lg:hidden),
  // so the main content shouldn't reserve any space for it.
  const sidebarHiddenOnDesktop = location.pathname.includes('/social-profile/');
  const reserveSidebarSpace = showSidebar && isLargeScreen && !sidebarHiddenOnDesktop;

  React.useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Mobile bottom nav (buyer "More" tab, etc.) opens the sidebar drawer
  // by dispatching this event — keeps the components decoupled.
  React.useEffect(() => {
    const onOpen = () => setIsMobileSidebarOpen(true);
    window.addEventListener('ca:openSidebar', onOpen);
    return () => window.removeEventListener('ca:openSidebar', onOpen);
  }, []);

  const handleSidebarToggle = (isExpanded: boolean) => {
    setSidebarWidth(isExpanded ? 288 : 80); // w-72 : w-20
  };

  // Determine mobile navigation type - parent pages use MobileHomeHeader
  const isParentPage = location.pathname === '/' ||
                       location.pathname === '/designers' ||
                       location.pathname === '/archives' ||
                       // Orders is a top-level destination (reached from the bottom
                       // nav), not a drilled-into detail — so it carries the parent
                       // header, not a back bar. Its sub-routes (/orders/<id>, …)
                       // stay child pages via `isOrderDetailPage` below.
                       location.pathname === '/orders';
  // "Child" pages are drilled into from a parent list and own their in-page
  // back navigation, so on mobile they drop the app chrome — no bottom nav and
  // no main header (logo + hamburger bar).
  //   • Social profile: a minimal floating header (back + hamburger) that
  //     overlays the cover photo, instead of the solid MobileBackNav bar.
  //   • Order/offer detail sub-routes (/orders/<id>, /orders/briefs/<id>,
  //     …/details, …/track): the page renders its own "Back" link, so we hide
  //     the app chrome entirely and let the page fill the screen.
  //   • Dress detail (/dress/<id>): a full-bleed hero carrying its own back,
  //     share and save buttons, and a fixed booking bar that must sit on the
  //     screen edge — so it also skips ChildMobileHeader below.
  const isProfilePage = location.pathname.includes('/social-profile/');
  const isOrderDetailPage = /^\/orders\/.+/.test(location.pathname);
  const isDressPage = /^\/dress\/.+/.test(location.pathname);
  const isChildPage = isProfilePage || isOrderDetailPage || isDressPage;
  const showMobileBackNav = !isParentPage && !isChildPage;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <DiscountBanner />
      <SuperAdminBar />
      {/* Desktop Navbar - Hidden on mobile */}
      <Navbar 
        showSidebarToggle={showSidebarToggle}
        isMobileSidebarOpen={isMobileSidebarOpen}
        onMobileSidebarToggle={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
      />
      {/* Mobile Navigation - Show home header for parent pages or back nav for child pages */}
      {isParentPage && (
        <MobileHomeHeader 
          onMobileSidebarToggle={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
        />
      )}
      {showMobileBackNav && (
        <MobileBackNav
          onMobileSidebarToggle={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
        />
      )}
      {/* The dress page paints its own controls over the hero (back, share,
          save), so it opts out of the shared child header — otherwise two back
          buttons stack in the same corner. */}
      {isChildPage && !isDressPage && (
        <ChildMobileHeader
          showSidebarToggle={showSidebarToggle}
          onMobileSidebarToggle={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
          // Profile overlays a cover photo (transparent); content pages like
          // order detail get a solid white bar.
          solid={isOrderDetailPage}
        />
      )}
      {showSidebar && (
        <Sidebar 
          onToggle={handleSidebarToggle}
          isMobileOpen={isMobileSidebarOpen}
          onMobileClose={() => setIsMobileSidebarOpen(false)}
        />
      )}
      <main
        className={`flex-grow transition-all duration-300 w-full ${className}`}
        style={{
          marginLeft: reserveSidebarSpace ? `${sidebarWidth}px` : '0',
          width: reserveSidebarSpace ? `calc(100% - ${sidebarWidth}px)` : '100%',
          // Reserve space for the mobile bottom nav, except on child pages
          // which hide it.
          paddingBottom: isLargeScreen ? (reserveSidebarSpace ? '6rem' : '0') : (isChildPage ? '0' : '5rem')
        }}
      >
        {children || <Outlet />}
      </main>
      {/* Desktop Footer */}
      <div className="hidden md:block">
        {showSidebar ? <AuthenticatedFooter /> : <Footer />}
      </div>
      {/* Mobile Bottom Navigation — hidden on child pages (profile, order detail). */}
      {!isChildPage && <MobileBottomNav />}
      {/* WhatsApp escape hatch. Picks its own routes — see TalkToDesignerFloat.
          Child pages carry the button inline next to their own CTA instead, so
          the floating one would be a second way to do the same thing. */}
      {!isChildPage && <TalkToDesignerFloat />}
    </div>
  );
};

export default Layout; 